import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

function AdminGallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [validItems, setValidItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
  const token = localStorage.getItem('token');


  const loadGallery = useCallback(() => {
    // For admin, get all items (including inactive)
    apiService.getGallery(false, token)
      .then(response => {
        // Remove duplicates based on file_path
        const uniqueItems = [];
        const seenPaths = new Set();
        
        response.data.gallery.forEach(item => {
          if (!seenPaths.has(item.file_path)) {
            seenPaths.add(item.file_path);
            uniqueItems.push(item);
          }
        });
        
        setGalleryItems(uniqueItems);
        // Start with all items, filter out broken ones as they fail to load
        setValidItems(uniqueItems);
      })
      .catch(error => console.error('Error loading gallery:', error));
  }, [token]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate all files first
    const invalidFiles = [];
    files.forEach((file, index) => {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = isVideo ? 100 : 10;
        invalidFiles.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB, max ${maxSizeMB}MB)`);
      }
    });

    if (invalidFiles.length > 0) {
      alert(`Some files are too large:\n${invalidFiles.join('\n')}`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress({});
    
    const results = {
      success: [],
      failed: []
    };

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${file.name}-${i}`;
      
      try {
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'uploading', fileName: file.name, progress: 0 }
        }));

        // Upload file
        const uploadResponse = await apiService.uploadFile(file, token);
        const filePath = uploadResponse.data.file.path;
        const fileType = uploadResponse.data.file.type;

        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'saving', fileName: file.name, progress: 50 }
        }));

        // Check if file_path already exists to prevent duplicates
        const existingItem = galleryItems.find(item => item.file_path === filePath);
        if (existingItem) {
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { status: 'failed', fileName: file.name, error: 'File already exists in gallery', progress: 0 }
          }));
          results.failed.push({ name: file.name, error: 'File already exists in gallery' });
          continue;
        }

        // Add to gallery
        await apiService.addGalleryItem({
          title: file.name,
          file_path: filePath,
          file_type: fileType
        }, token);

        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'completed', fileName: file.name, progress: 100 }
        }));

        results.success.push(file.name);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        const errorMessage = error.response?.data?.message || error.message || 'Error uploading file';
        
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'failed', fileName: file.name, error: errorMessage, progress: 0 }
        }));

        results.failed.push({ name: file.name, error: errorMessage });
      }
    }

    // Reload gallery after all uploads
    loadGallery();

    // Show summary
    let message = '';
    if (results.success.length > 0) {
      message += `Successfully uploaded ${results.success.length} file(s):\n${results.success.join(', ')}\n\n`;
    }
    if (results.failed.length > 0) {
      message += `Failed to upload ${results.failed.length} file(s):\n${results.failed.map(f => `${f.name}: ${f.error}`).join('\n')}`;
    }
    
    if (message) {
      alert(message);
    }

    // Clear progress after 3 seconds
    setTimeout(() => {
      setUploadProgress({});
    }, 3000);

    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const handleToggleActive = (item) => {
    const newActiveStatus = !item.is_active;
    
    apiService.updateGalleryItem({
      id: item.id,
      is_active: newActiveStatus,
      display_order: item.display_order || 0
    }, token)
      .then(() => {
        // Update local state
        setGalleryItems(prev => prev.map(i => 
          i.id === item.id ? { ...i, is_active: newActiveStatus } : i
        ));
        setValidItems(prev => prev.map(i => 
          i.id === item.id ? { ...i, is_active: newActiveStatus } : i
        ));
      })
      .catch(error => {
        console.error('Error updating gallery item:', error);
        alert('Failed to update item status. Please try again.');
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      apiService.deleteGalleryItem(id, token)
        .then(() => loadGallery())
        .catch(error => console.error('Error deleting item:', error));
    }
  };

  const handleItemError = (itemId) => {
    // Remove item from valid items list when it fails to load
    setValidItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Filter items based on active status
  const filteredItems = validItems.filter(item => {
    if (filterActive === 'active') return item.is_active;
    if (filterActive === 'inactive') return !item.is_active;
    return true; // 'all'
  });

  return (
    <div>
      <h2>Gallery Management</h2>
      
      <div className="admin-card">
        <div className="file-upload-area" onClick={() => document.getElementById('fileInput').click()}>
          <input
            id="fileInput"
            type="file"
            accept="image/*,video/*"
            onChange={handleFileUpload}
            disabled={uploading}
            multiple
          />
          <p style={{fontSize: '1.2rem', marginBottom: '10px'}}>
            {uploading ? 'Uploading...' : 'Click to upload image(s) or video(s) (Multiple files supported)'}
          </p>
          <p style={{color: '#999'}}>Supported formats: JPG, PNG, GIF (max 10MB), MP4, WEBM (max 100MB)</p>
          <p style={{color: '#666', fontSize: '0.9rem', marginTop: '5px'}}>You can select multiple files at once</p>
          <p style={{color: '#999', fontSize: '0.85rem', marginTop: '5px'}}>Duplicate files will be automatically filtered</p>
        </div>

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div style={{marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '4px'}}>
            <h4 style={{marginBottom: '10px'}}>Upload Progress:</h4>
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} style={{marginBottom: '10px', padding: '10px', background: 'white', borderRadius: '4px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                  <span style={{fontSize: '0.9rem', fontWeight: '500'}}>{progress.fileName}</span>
                  <span style={{
                    fontSize: '0.85rem',
                    color: progress.status === 'completed' ? '#28a745' : 
                           progress.status === 'failed' ? '#dc3545' : '#007bff'
                  }}>
                    {progress.status === 'uploading' && 'Uploading...'}
                    {progress.status === 'saving' && 'Saving...'}
                    {progress.status === 'completed' && '✓ Completed'}
                    {progress.status === 'failed' && '✗ Failed'}
                  </span>
                </div>
                {progress.status !== 'failed' && (
                  <div style={{width: '100%', height: '6px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden'}}>
                    <div style={{
                      width: `${progress.progress}%`,
                      height: '100%',
                      background: progress.status === 'completed' ? '#28a745' : '#007bff',
                      transition: 'width 0.3s'
                    }}></div>
                  </div>
                )}
                {progress.status === 'failed' && progress.error && (
                  <div style={{fontSize: '0.85rem', color: '#dc3545', marginTop: '5px'}}>
                    Error: {progress.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="admin-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
          <h3 style={{margin: 0}}>Gallery Items</h3>
          <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
            <span style={{fontSize: '0.9rem', color: '#666'}}>Filter:</span>
            <button
              onClick={() => setFilterActive('all')}
              className={filterActive === 'all' ? 'btn-admin btn-edit' : 'btn-admin'}
              style={{
                padding: '6px 12px',
                fontSize: '0.85rem',
                background: filterActive === 'all' ? 'var(--primary-blue)' : '#f5f5f5',
                color: filterActive === 'all' ? '#fff' : '#333',
                border: '1px solid #ddd'
              }}
            >
              All ({validItems.length})
            </button>
            <button
              onClick={() => setFilterActive('active')}
              className={filterActive === 'active' ? 'btn-admin btn-edit' : 'btn-admin'}
              style={{
                padding: '6px 12px',
                fontSize: '0.85rem',
                background: filterActive === 'active' ? '#28a745' : '#f5f5f5',
                color: filterActive === 'active' ? '#fff' : '#333',
                border: '1px solid #ddd'
              }}
            >
              Active ({validItems.filter(i => i.is_active).length})
            </button>
            <button
              onClick={() => setFilterActive('inactive')}
              className={filterActive === 'inactive' ? 'btn-admin btn-edit' : 'btn-admin'}
              style={{
                padding: '6px 12px',
                fontSize: '0.85rem',
                background: filterActive === 'inactive' ? '#6c757d' : '#f5f5f5',
                color: filterActive === 'inactive' ? '#fff' : '#333',
                border: '1px solid #ddd'
              }}
            >
              Inactive ({validItems.filter(i => !i.is_active).length})
            </button>
          </div>
        </div>
        
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px'}}>
          {filteredItems.length === 0 ? (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999'}}>
              {filterActive === 'active' ? 'No active items. Toggle items to make them visible on the frontend.' :
               filterActive === 'inactive' ? 'No inactive items.' :
               'No gallery items found. Upload images or videos to get started.'}
            </div>
          ) : (
            filteredItems.map(item => (
              <div 
                key={item.id} 
                className="gallery-item-wrapper"
                style={{
                  position: 'relative', 
                  border: item.is_active ? '3px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px', 
                  overflow: 'hidden',
                  opacity: item.is_active ? 1 : 0.7
                }}
              >
                {item.file_type === 'image' ? (
                  <img 
                    src={item.file_path} 
                    alt={item.title || 'Gallery image'} 
                    style={{width: '100%', height: '200px', objectFit: 'cover', display: 'block'}}
                    onError={(e) => {
                      console.error('Image failed to load:', item.file_path);
                      handleItemError(item.id);
                    }}
                  />
                ) : (
                  <video 
                    src={item.file_path} 
                    style={{width: '100%', height: '200px', objectFit: 'cover', display: 'block'}}
                    muted
                    playsInline
                    onError={(e) => {
                      console.error('Video failed to load:', item.file_path);
                      handleItemError(item.id);
                    }}
                  />
                )}
                
                {/* Active/Inactive Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  background: item.is_active ? 'rgba(40, 167, 69, 0.95)' : 'rgba(108, 117, 125, 0.95)',
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  zIndex: 10,
                  pointerEvents: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  {item.is_active ? '✓ Active' : '○ Hidden'}
                </div>
                
                {/* Hover overlay indicator */}
                <div 
                  className="gallery-item-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: item.file_type === 'video' 
                      ? 'rgba(46, 139, 192, 0.85)' 
                      : 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: 'none'
                  }}
                >
                  <div style={{
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    {item.file_type === 'video' ? (
                      <>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5v14l11-7z" fill="currentColor"/>
                        </svg>
                        VIDEO
                      </>
                    ) : (
                      <>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        IMAGE
                      </>
                    )}
                  </div>
                </div>
                
                {/* Toggle Active Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleActive(item);
                  }}
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '10px',
                    right: '10px',
                    background: item.is_active ? '#28a745' : '#6c757d',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    zIndex: 10,
                    transition: 'background 0.3s',
                    width: 'calc(100% - 20px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = item.is_active ? '#218838' : '#5a6268';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = item.is_active ? '#28a745' : '#6c757d';
                  }}
                >
                  {item.is_active ? '✓ Show on Frontend' : '○ Hide from Frontend'}
                </button>
                
                <button 
                  className="btn-admin btn-delete" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                  style={{position: 'absolute', top: '10px', right: '10px', zIndex: 10, fontSize: '0.8rem', padding: '4px 8px'}}
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
        
        <div style={{marginTop: '20px', padding: '15px', background: '#e7f3ff', borderRadius: '4px', fontSize: '0.9rem', color: '#004085'}}>
          <strong>💡 Tip:</strong> Only items marked as "Active" will be displayed on the frontend gallery page. Toggle items ON/OFF to control what visitors see.
        </div>
      </div>
    </div>
  );
}

export default AdminGallery;
